import React, { useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { RootState } from "../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  fetchData,
} from "../../store/actions";
import useAxiosInterceptor from "../layout/AxiosInstance";
import { managerAssignmentHistoryUrl, viewUsers } from "../../Service/Apis";
import CustomNoRowsOverlay from "../Data/NoRowsOverlayComponent";

// ---------- types ----------

interface AssignmentRecord {
  id: string;
  employee: string;
  employee_name: string;
  manager: string | null;
  manager_name: string | null;
  manager_level: "M1" | "M2";
  project: string | null;
  project_name: string | null;
  from_date: string | null;
  till_date: string | null;
  billable: string | null;
  assignment_type: string | null;
  is_active: boolean;
}

interface UserOption {
  id: string;
  first_name: string;
  last_name: string;
  employee_code: string;
}

interface Props {
  /** UUID of the employee whose history to display.
   *  When omitted the component renders in standalone-page mode
   *  and shows an employee selector at the top. */
  employeeId?: string;
  /** Optional employee display name for the section heading */
  employeeName?: string;
}

// ---------- helpers ----------

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS: number[] = Array.from({ length: 5 }, (_, i) => currentYear - i);

const fmt = (dateStr: string | null): string => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// ---------- component ----------

const ManagerAssignmentHistory: React.FC<Props> = ({ employeeId, employeeName }) => {
  const axiosInstance = useAxiosInterceptor();
  const dispatch: ThunkDispatch<RootState, null, FetchDataSuccessAction | FetchDataFailureAction> =
    useDispatch();

  // standalone-page state — only used when employeeId is not passed as a prop
  const isStandalone = employeeId === undefined;
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);

  const resolvedEmployeeId = isStandalone ? (selectedUser?.id ?? "") : employeeId;
  const resolvedEmployeeName = isStandalone
    ? selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : ""
    : employeeName ?? "";

  const [rows, setRows] = useState<AssignmentRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState<string>(String(currentYear));
  const [fromDateFilter, setFromDateFilter] = useState<string>("");
  const [toDateFilter, setToDateFilter] = useState<string>("");

  // fetch user list only in standalone mode
  useEffect(() => {
    if (!isStandalone) return;
    dispatch(fetchData(viewUsers))
      .then((data: any) => setUsers(Array.isArray(data) ? data : []))
      .catch(() => setUsers([]));
  }, [isStandalone, dispatch]);

  const fetchHistory = useCallback(async () => {
    if (!resolvedEmployeeId) return;
    setLoading(true);
    try {
      const params: Record<string, string> = { employee_id: resolvedEmployeeId };
      if (year !== "ALL") params.year = year;

      const token = localStorage.getItem("token");
      const response = await axiosInstance.get<AssignmentRecord[]>(managerAssignmentHistoryUrl, {
        params,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      setRows(response.data);
    } catch (err) {
      console.error("Failed to fetch assignment history:", err);
    } finally {
      setLoading(false);
    }
  }, [resolvedEmployeeId, year]); // axiosInstance intentionally omitted — it has no stable reference and would cause an infinite fetch loop

  const filteredRows = rows.filter((row) => {
    if (fromDateFilter && row.from_date && row.from_date < fromDateFilter) return false;
    if (toDateFilter && row.from_date && row.from_date > toDateFilter) return false;
    return true;
  });

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const columns: GridColDef[] = [
    {
      field: "manager_level",
      headerName: "Level",
      width: 75,
      align: "center",
      headerAlign: "center",
      renderHeader: () => <strong style={{ fontSize: 14 }}>Level</strong>,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === "M1" ? "primary" : "secondary"}
          variant="outlined"
        />
      ),
    },
    {
      field: "manager_name",
      headerName: "Manager Name",
      flex: 1.2,
      headerAlign: "center",
      align: "center",
      renderHeader: () => <strong style={{ fontSize: 14 }}>Manager Name</strong>,
      renderCell: (params) => params.value ?? "—",
    },
    {
      field: "from_date",
      headerName: "From Date",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderHeader: () => <strong style={{ fontSize: 14 }}>From Date</strong>,
      renderCell: (params) => fmt(params.value),
    },
    {
      field: "till_date",
      headerName: "To Date",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderHeader: () => <strong style={{ fontSize: 14 }}>To Date</strong>,
      renderCell: (params) =>
        params.row.is_active && !params.value ? <em style={{ color: "#4caf50" }}>Present</em> : fmt(params.value),
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 90,
      headerAlign: "center",
      align: "center",
      renderHeader: () => <strong style={{ fontSize: 14 }}>Status</strong>,
      renderCell: (params) =>
        params.value ? (
          <Chip label="Active" size="small" color="success" />
        ) : (
          <Chip label="Past" size="small" color="default" />
        ),
    },
    {
      field: "assignment_type",
      headerName: "Manager Type",
      width: 115,
      headerAlign: "center",
      align: "center",
      renderHeader: () => <strong style={{ fontSize: 14 }}>Manager Type</strong>,
      renderCell: (params) => {
        const TYPE_LABELS: Record<string, string> = {
          qa: "QA",
          project: "Project",
          product: "Product",
          reporting: "Reporting",
        };
        const label = params.value ? (TYPE_LABELS[params.value] ?? params.value) : null;
        return label ? (
          <Tooltip title={label}>
            <span>{label}</span>
          </Tooltip>
        ) : (
          "—"
        );
      },
    },
    {
      field: "billable",
      headerName: "Billable",
      width: 85,
      headerAlign: "center",
      align: "center",
      renderHeader: () => <strong style={{ fontSize: 14 }}>Billable</strong>,
      renderCell: (params) =>
        params.value ? (
          <span style={{ textTransform: "capitalize" }}>{params.value}</span>
        ) : (
          "—"
        ),
    },
    {
      field: "project_name",
      headerName: "Project Name",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      renderHeader: () => <strong style={{ fontSize: 14 }}>Project Name</strong>,
      renderCell: (params) =>
        params.value ? (
          <span
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: 1.4,
              padding: "6px 0",
              textAlign: "center",
              width: "100%",
            }}
          >
            {params.value}
          </span>
        ) : (
          "—"
        ),
    },
  ];

  const historyContent = (
    <Box sx={{ width: "100%" }}>
      {resolvedEmployeeName && (
        <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
          Assignment History — {resolvedEmployeeName}
        </Typography>
      )}

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Year</InputLabel>
          <Select
            value={year}
            label="Year"
            onChange={(e: SelectChangeEvent) => setYear(e.target.value)}
          >
            <MenuItem value="ALL">All Years</MenuItem>
            {YEAR_OPTIONS.map((y) => (
              <MenuItem key={y} value={String(y)}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="From Date"
          type="date"
          value={fromDateFilter}
          onChange={(e) => setFromDateFilter(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ style: { colorScheme: "light" } }}
          sx={{ minWidth: 160 }}
        />

        <TextField
          size="small"
          label="To Date"
          type="date"
          value={toDateFilter}
          onChange={(e) => setToDateFilter(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            min: fromDateFilter || undefined,
            style: { colorScheme: "light" },
          }}
          sx={{ minWidth: 160 }}
        />
      </Box>

      <Paper elevation={2} sx={{ width: "100%" }}>
        <DataGrid
          autoHeight
          loading={loading}
          rows={filteredRows}
          columns={columns}
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          getRowHeight={() => "auto"}
          sx={{
            width: "100%",
            "--DataGrid-overlayHeight": "40vh",
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f0f4f0",
              borderBottom: "2px solid #d0e0d0",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
              fontSize: 13,
            },
            "& .MuiDataGrid-row": {
              borderBottom: "1px solid #eef2ee",
              "&:hover": { backgroundColor: "#f7fbf7" },
              "&:nth-of-type(even)": { backgroundColor: "#fafcfa" },
            },
            "& .MuiDataGrid-cell": {
              alignItems: "center",
              display: "flex",
              padding: "10px 12px",
              border: "none",
              fontSize: 13,
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "2px solid #d0e0d0",
            },
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pageSizeOptions={[5, 10, 25]}
          pagination
          slots={{
            toolbar: GridToolbar,
            noRowsOverlay: CustomNoRowsOverlay,
            noResultsOverlay: CustomNoRowsOverlay,
          }}
          slotProps={{
            toolbar: {
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
              showQuickFilter: true,
              style: { margin: "0.5rem" },
            },
          }}
        />
      </Paper>
    </Box>
  );

  // ---- embedded mode (employeeId passed as prop) ----
  if (!isStandalone) {
    return historyContent;
  }

  // ---- standalone page mode ----
  return (
    <Box sx={{ mt: 12, mb: 10, mx: "auto", maxWidth: 1100, px: 2 }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
        Manager History
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
          Select Employee
        </Typography>
        <Autocomplete
          options={users}
          getOptionLabel={(u) =>
            `${u.first_name} ${u.last_name} (${u.employee_code})`
          }
          value={selectedUser}
          onChange={(_, val) => {
            setSelectedUser(val);
            setRows([]);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Employee"
              size="small"
              placeholder="Search by name or code…"
            />
          )}
          sx={{ maxWidth: 420 }}
        />
      </Paper>

      {selectedUser ? (
        historyContent
      ) : (
        <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
          <Typography variant="body1">
            Select an employee above to view their manager assignment history.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ManagerAssignmentHistory;
